


import React, { useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { saveFeedback, deleteUserData } from '../services/firestore';
import { CheckIcon } from './icons';

interface DeleteAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: firebase.User | null;
}

const deletionReasons = [
    { value: 'too_complicated', label: 'La aplicación es demasiado complicada de usar.' },
    { value: 'missing_features', label: 'No tiene las funcionalidades que necesito.' },
    { value: 'found_alternative', label: 'He encontrado una alternativa mejor.' },
    { value: 'privacy_concerns', label: 'Me preocupan mis datos o mi privacidad.' },
    { value: 'no_longer_needed', label: 'Ya no necesito una aplicación de este tipo.' },
    { value: 'other', label: 'Otro motivo...' },
];

const CONFIRMATION_TEXT = 'BORRAR MI CUENTA';

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose, user }) => {
    const [step, setStep] = useState(1);
    const [password, setPassword] = useState('');
    const [reason, setReason] = useState('');
    const [comments, setComments] = useState('');
    const [confirmationText, setConfirmationText] = useState('');
    
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleReauthentication = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !user.email) return;

        setLoading(true);
        setError(null);
        try {
            const credential = firebase.auth.EmailAuthProvider.credential(user.email, password);
            await (user as any).reauthenticateWithCredential(credential);
            setStep(2);
        } catch (err: any) {
            console.error("Reauthentication failed:", err);
             if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError('La contraseña es incorrecta.');
            } else {
                setError('Ha ocurrido un error al verificar tu identidad. Inténtalo de nuevo.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleProceedToFinalStep = () => {
        if (!reason) {
            setError("Por favor, selecciona un motivo para continuar.");
            return;
        }
        setError(null);
        setStep(3);
    };

    const handleDeleteAccount = async () => {
        if (!user || confirmationText !== CONFIRMATION_TEXT) return;
        
        setLoading(true);
        setError(null);
        try {
            // Step 1: Attempt to save feedback (non-critical, continue on failure)
            try {
                await saveFeedback({ reason, comments });
            } catch (feedbackError) {
                console.warn("Could not save user feedback, proceeding with deletion:", feedbackError);
            }

            // Step 2: Delete user data from Firestore (critical, may fail on security rules)
            await deleteUserData(user.uid);

            // Step 3: ONLY if data deletion is successful, delete the auth user
            await user.delete();
            
            // Step 4: Show success screen
            setStep(4);
            setLoading(false);
            
        } catch (err: any)
        {
            console.error("Account deletion failed:", err);
            if (err.code && (err.code.includes('permission-denied') || err.code.includes('unauthenticated'))) {
                 setError('Error al borrar los datos. Es posible que las reglas de seguridad de Firestore no lo permitan. Contacta con el soporte.');
            } else {
                setError('No se pudo completar la eliminación. Por favor, contacta con el soporte.');
            }
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset state on close
        setStep(1);
        setPassword('');
        setReason('');
        setComments('');
        setConfirmationText('');
        setError(null);
        setLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    const renderStepContent = () => {
        switch (step) {
            case 1: // Re-authentication
                return (
                    <>
                        <h2 className="text-2xl font-bold text-red-500 mb-4">Borrar Cuenta Permanentemente</h2>
                        <p className="text-light-text-secondary dark:text-text-secondary mb-4">
                            Estás a punto de eliminar tu cuenta. Esta acción es <strong>irreversible</strong> y se borrarán todos tus datos.
                        </p>
                        <p className="text-light-text-secondary dark:text-text-secondary mb-6">
                            Por seguridad, por favor, introduce tu contraseña para continuar.
                        </p>
                        <form onSubmit={handleReauthentication}>
                            <label htmlFor="password-confirm" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Contraseña</label>
                            <input
                                type="password"
                                id="password-confirm"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
                            <div className="flex justify-end gap-4 mt-6">
                                <button type="button" onClick={handleClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                                <button type="submit" disabled={loading || !password} className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:bg-gray-500">
                                    {loading ? 'Verificando...' : 'Continuar'}
                                </button>
                            </div>
                        </form>
                    </>
                );
            case 2: // Feedback
                return (
                    <>
                        <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary mb-4">Lamentamos que te vayas</h2>
                        <p className="text-light-text-secondary dark:text-text-secondary mb-6">
                            ¿Podrías decirnos por qué has decidido borrar tu cuenta? Tus comentarios son anónimos y muy valiosos para nosotros.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="reason" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Motivo principal</label>
                                <select id="reason" value={reason} onChange={e => setReason(e.target.value)} required className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2">
                                    <option value="" disabled>Selecciona una opción...</option>
                                    {deletionReasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            {reason === 'other' && (
                                <div>
                                    <label htmlFor="comments" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Por favor, danos más detalles (opcional)</label>
                                    <textarea
                                        id="comments"
                                        value={comments}
                                        onChange={e => setComments(e.target.value)}
                                        rows={3}
                                        className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2"
                                        placeholder="Tus comentarios nos ayudan a mejorar..."
                                    />
                                </div>
                            )}
                        </div>
                        {error && <p className="text-sm text-red-400 mt-4">{error}</p>}
                        <div className="flex justify-end gap-4 mt-8">
                            <button type="button" onClick={handleClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button onClick={handleProceedToFinalStep} disabled={!reason} className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:bg-gray-500">
                                Siguiente
                            </button>
                        </div>
                    </>
                );
            case 3: // Final Confirmation
                 return (
                    <>
                        <h2 className="text-2xl font-bold text-red-500 mb-4">Confirmación Final</h2>
                        <p className="text-light-text-secondary dark:text-text-secondary mb-6">
                            Esta es tu última oportunidad. Para confirmar que entiendes que <strong>todos tus datos serán borrados permanentemente</strong>, por favor, escribe la siguiente frase en el campo de abajo:
                        </p>
                        <div className="text-center bg-gray-200 dark:bg-gray-800 p-2 rounded-md my-4">
                            <code className="text-lg font-bold text-light-text-primary dark:text-text-primary tracking-wider">{CONFIRMATION_TEXT}</code>
                        </div>
                        <input
                            type="text"
                            value={confirmationText}
                            onChange={e => setConfirmationText(e.target.value)}
                            className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            aria-label="Confirmación de borrado de cuenta"
                        />
                        {error && <p className="text-sm text-red-400 mt-4">{error}</p>}
                        <div className="flex justify-end gap-4 mt-8">
                            <button type="button" onClick={handleClose} className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Cancelar</button>
                            <button onClick={handleDeleteAccount} disabled={loading || confirmationText !== CONFIRMATION_TEXT} className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                {loading ? 'Borrando...' : 'Confirmar y Borrar Todo'}
                            </button>
                        </div>
                    </>
                );
            case 4: // Success screen
                return (
                    <>
                        <div className="text-center">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50 mb-4">
                               <CheckIcon className="h-10 w-10 text-secondary" />
                            </div>
                            <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary mb-4">Cuenta eliminada con éxito</h2>
                            <p className="text-light-text-secondary dark:text-text-secondary mb-2">
                                Todos tus datos han sido borrados de forma permanente. Te hemos enviado un correo de confirmación.
                            </p>
                             <p className="text-light-text-secondary dark:text-text-secondary mb-6">
                                Gracias por haber formado parte de Mis Cuentas 360. ¡Esperamos verte de nuevo!
                            </p>
                        </div>
                        <div className="flex justify-center mt-6">
                            <button type="button" onClick={handleClose} className="w-full px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold">
                                Entendido
                            </button>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center" onClick={handleClose}>
            <div className="bg-light-surface dark:bg-surface rounded-lg shadow-xl p-8 w-[95%] sm:w-full max-w-lg border border-light-border dark:border-border" onClick={e => e.stopPropagation()}>
                {renderStepContent()}
            </div>
        </div>
    );
};

export default DeleteAccountModal;